/**
 * Best-effort mime type from a filename extension.
 *
 * Used where only a filename is available - an entity link to a file carries
 * its name as the label but no mime type - and the preview modal branches on
 * mime type to decide how to render.
 *
 * Returns '' when the extension is unknown or absent. That is deliberate: the
 * modal's fallback is a download button, which is the right outcome for a file
 * we cannot confidently render. Guessing wrong would show a broken renderer
 * instead.
 */
const BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  eml: 'message/rfc822',
  msg: 'application/vnd.ms-outlook',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  json: 'application/json',
  zip: 'application/zip',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

export function mimeFromFilename(filename: string): string {
  if (!filename) return '';
  const idx = filename.lastIndexOf('.');
  // No dot, a leading dot with nothing before it (a dotfile), or a trailing
  // dot: none of these carry a usable extension.
  if (idx <= 0 || idx === filename.length - 1) return '';
  const ext = filename.slice(idx + 1).toLowerCase();
  return BY_EXTENSION[ext] ?? '';
}
